import FormError from '@Components/FormError/FormError';
import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import AccessoriesServices from '@Services/Accessories.services.ts';
import { MultiDropOption } from '@Types/Form.types';
import { DROPDOWN_DEFAULT } from '@Utils/constants';
import { formatRequired } from '@Utils/form.utils';
import { formatPeopleDropdown } from '@Utils/format.utils.tsx';
import { handleRequestError } from '@Utils/request.utils';
import { getUserWithLocation } from '@Utils/user.utils';
import { useApolloClient } from '@apollo/client';
import { ErrorDisplay, LogoLoader, MultiSelectDropdown, Toast } from '@kg-internal/pv3-uikit';
import { GetEmployeeByEmailDocument, GetEmployeeByEmailQuery, useGetEmployeesQuery } from '__generated__/graph';
import cx from 'classnames';
import { useState } from 'react';

type Props = {
  showModal: boolean;
  onClose: () => void;
  onSuccess: () => void;
  accesoryId?: string;
};

const AccessoryAssignModal = ({ showModal, onClose, onSuccess, accesoryId }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [fieldErrorMessage, setFieldErrorMessage] = useState<string | undefined>(undefined);
  const [people, setPeople] = useState<MultiDropOption[]>([]);
  const client = useApolloClient();

  const onSubmitSuccess = () => {
    setPeople([]);
    setFieldErrorMessage(undefined);
    onSuccess();
    onClose();
  };

  const handleSubmit = async () => {
    setErrorMessage(undefined);
    if (!people.length) {
      setFieldErrorMessage('This field is required.');
      return;
    }
    let hasError = false;

    const kgUsers = await Promise.all(
      people.map(async (user) => {
        const { data: kgUser, error } = await client.query<GetEmployeeByEmailQuery>({
          query: GetEmployeeByEmailDocument,
          variables: { email: user.value },
        });

        if (error) {
          hasError = true;
          Toast.warning('Could not fetch employee.');
        }

        return kgUser.person;
      }),
    );

    if (!hasError) {
      Promise.all(
        kgUsers.map((kgUser) => AccessoriesServices.assignAccessory(accesoryId, getUserWithLocation(kgUser!))),
      )
        .then(() => {
          Toast.success('Accessory assigned successfully.');
          onSubmitSuccess();
        })
        .catch((errorResponse) => handleRequestError(errorResponse, setErrorMessage, true));
    }
  };

  const handleAccessoryModalClose = () => {
    setPeople([]);
    onClose();
  };

  const { loading, error, data } = useGetEmployeesQuery();

  if (loading) {
    return (
      <div className='modal--pt-30vh modal--w-30vw modal--fit-content'>
        <ModalWrapper
          title='Assign Accessories'
          show={showModal}
          handleClose={onClose}
          disablePrimary
          primaryButtonText='Confirm'>
          <LogoLoader inline />
        </ModalWrapper>
      </div>
    );
  }

  if (error) {
    setErrorMessage(error.message);
  }

  const getForm = () => (
    <form>
      <div className='grid'>
        <div className='col-12'>
          <span
            className={cx({
              'form-error': fieldErrorMessage,
            })}>
            <MultiSelectDropdown<string>
              options={formatPeopleDropdown(data?.peopleV2)}
              values={people}
              selectOption={(selected) => setPeople((currentPeople) => [...currentPeople, selected])}
              removeOption={(removed) =>
                setPeople((currentPeople) => currentPeople.filter((curr) => curr.value !== removed.value))
              }
              fullWidth
              label={formatRequired('Owner')}
              value={DROPDOWN_DEFAULT}
              onChange={() => setFieldErrorMessage(undefined)}
            />
            <FormError error={fieldErrorMessage} />
          </span>
        </div>
      </div>
    </form>
  );
  return (
    <div className='modal--pt-30vh modal--w-30vw modal--fit-content'>
      <ModalWrapper
        title='Assign Accessories'
        primaryButtonText='Confirm'
        show={showModal}
        handleClose={handleAccessoryModalClose}
        primaryAction={() => {
          handleSubmit();
        }}>
        {errorMessage ? <ErrorDisplay inline details={errorMessage} /> : getForm()}
      </ModalWrapper>
    </div>
  );
};

export default AccessoryAssignModal;
